import { useEffect, useMemo, useState } from "react";
import { List, Briefcase, CheckSquare, Phone, IndianRupee, MapPin, Clock, Sparkles, XCircle, CheckCircle2, Lock, ShieldCheck, UserCheck, Hourglass, Users, Navigation, UserPlus, Gift, LogOut, Rocket, User as UserIcon, ChevronRight, MessagesSquare } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BankStaffShell, { HeaderPill } from "@/components/app/BankStaffShell";
import BankFuelMeter from "@/components/app/BankFuelMeter";
import ReferralButton from "@/components/app/ReferralButton";
import BankStaffReferPage from "@/pages/bank_staff/BankStaffReferPage";
import BankStaffEditProfileDialog from "@/components/app/BankStaffEditProfileDialog";
import UniversalLeadAdd from "@/components/app/UniversalLeadAdd";
import BoostCountdownCard from "@/components/app/BoostCountdownCard";
import RejectionHistory from "@/components/app/RejectionHistory";

const REASONS = ["CIBIL Low", "Property Value", "Legal Issue", "Overdue Loans", "Documents Insufficient", "Other"];
const PAGE_TITLES = { refer: "Refer & Earn", wanted: "Wanted List", taken: "My Taken (24h SLA)", ok: "My OK Cases (30d)", profile: "Profile" };
const APPROVALS = [
  { key: "cibil", label: "CIBIL" },
  { key: "legal", label: "Legal" },
  { key: "tech",  label: "Tech"  },
];

// ============================================================================
// Countdown hook + SLA badges
// ============================================================================
function useCountdown(deadlineIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadlineIso) return { h: 0, m: 0, s: 0, expired: false, ms: 0 };
  const ms = new Date(deadlineIso).getTime() - now;
  const expired = ms <= 0;
  const abs = Math.max(0, ms);
  return {
    h: Math.floor(abs / 3_600_000),
    m: Math.floor((abs % 3_600_000) / 60_000),
    s: Math.floor((abs % 60_000) / 1000),
    expired, ms,
  };
}

function SLABadge24h({ takenAt }) {
  const deadline = takenAt ? new Date(new Date(takenAt).getTime() + 24 * 3600 * 1000).toISOString() : null;
  const { h, m, expired, ms } = useCountdown(deadline);
  const totalH = ms / 3600000;
  if (expired) {
    return (
      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-red-500/15 text-[#FF6B6B] border border-red-500/30 inline-flex items-center gap-1" data-testid="sla-overdue">
        <Clock className="w-3 h-3"/> Overdue
      </span>
    );
  }
  const cls = totalH < 2
    ? "bg-red-500/15 text-[#FF6B6B] border-red-500/30"
    : totalH < 12
      ? "bg-[#FF8C00]/15 text-[#FF8C00] border-[#FF8C00]/30"
      : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border inline-flex items-center gap-1 font-mono-alt ${cls}`} data-testid="sla-timer">
      <Clock className="w-3 h-3"/> {h}h {String(m).padStart(2, "0")}m left
    </span>
  );
}

function Timer30d({ okAt }) {
  const deadline = okAt ? new Date(new Date(okAt).getTime() + 30 * 86400 * 1000).toISOString() : null;
  const { ms, expired } = useCountdown(deadline);
  const days = Math.floor(ms / 86400000);
  const cls = expired ? "bg-red-500/15 text-red-300 border-red-500/30" :
              days < 5 ? "bg-red-500/15 text-red-300 border-red-500/30" :
              days < 15 ? "bg-[#FF8C00]/15 text-[#FF8C00] border-[#FF8C00]/30" :
                          "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${cls} font-mono-alt inline-flex items-center gap-1`}>
      <Clock className="w-3 h-3"/> {expired ? "OVERDUE" : `${days}d left`}
    </span>
  );
}

// ============================================================================
// Shared Add Customer + Referral cards (mobile-optimised, no overflow)
// ============================================================================
function AddCustomerHeaderPill({ onOpen }) {
  return (
    <HeaderPill onClick={onOpen} testId="header-add-customer">
      <UserPlus className="w-4 h-4"/> Add Customer
    </HeaderPill>
  );
}

// ============================================================================
// Wanted List Page
// ============================================================================
function WantedCard({ c, onTake, onRequestAccess }) {
  const status = c.bank_assignment_status || "none";
  const myAssigned = !!c.my_assigned;
  const myRequested = !!c.my_requested;
  const masked = !!c._pii_masked;
  const reqCount = Number(c.required_staff_count || 0);
  const assignedNames = c.assigned_bank_staff_names || "";
  return (
    <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid={`wanted-${c.lead_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-mono-alt text-[#C5A028] text-xs">{c.lead_id}</div>
        {masked ? (
          <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[9px]" data-testid={`pii-lock-${c.lead_id}`}>
            <Lock className="w-2.5 h-2.5 mr-1"/> PII Locked
          </Badge>
        ) : (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px]">
            <ShieldCheck className="w-2.5 h-2.5 mr-1"/> Unlocked
          </Badge>
        )}
      </div>
      <div className="font-heading text-xl text-white leading-tight break-words">
        {masked && <Lock className="w-4 h-4 text-slate-500 inline mr-1.5 align-baseline"/>}
        <span className={masked ? "tracking-widest text-slate-300" : ""}>{c.name}</span>
      </div>
      <div className="mt-2 space-y-1 text-sm text-slate-400">
        <div className="text-white flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5"/>{parseFloat(c.amount).toLocaleString("en-IN")} · <span className="text-slate-400">{c.loan_type}</span></div>
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{c.district}</div>
        {!masked && c.phone && (
          <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-emerald-300 hover:underline">
            <Phone className="w-3.5 h-3.5"/><span className="font-mono-alt">{c.phone}</span>
          </a>
        )}
      </div>
      {masked && (
        <div className="mt-3 rounded-lg border border-slate-700 bg-[#0F1424] px-2.5 py-2 text-[11px] text-slate-400 flex items-start gap-2" data-testid={`pii-hint-${c.lead_id}`}>
          <Lock className="w-3 h-3 mt-0.5 text-slate-500 shrink-0"/>
          <div>Customer details hidden. <span className="text-slate-300">Request access</span> to view name & phone.</div>
        </div>
      )}
      {c.rejections && c.rejections.length > 0 && <RejectionHistory lead={c} compact/>}

      {status === "assigned" && myAssigned && (
        <Button
          onClick={() => onTake(c.lead_id)}
          data-testid={`take-${c.lead_id}`}
          className="mt-4 w-full h-12 bg-[#FF8C00] hover:bg-[#e77e00] text-slate-900 font-bold rounded-full">
          Take This Case
        </Button>
      )}
      {status === "assigned" && !myAssigned && (
        <div className="mt-4 rounded-md border border-slate-700 bg-[#0F1424] px-3 py-2.5 text-xs text-slate-400 flex items-start gap-2">
          <UserCheck className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <div>Assigned to <span className="text-slate-200 font-semibold">{assignedNames || "another staff"}</span></div>
        </div>
      )}
      {status === "pending_assignment" && myRequested && (
        <div className="mt-4 rounded-md border border-[#C5A028]/40 bg-[#C5A028]/10 px-3 py-2.5 text-xs text-[#C5A028] flex items-center gap-2" data-testid={`awaiting-${c.lead_id}`}>
          <Hourglass className="w-3.5 h-3.5 shrink-0"/> Requested {reqCount || 1} · Awaiting telecaller approval
        </div>
      )}
      {(status === "pending_assignment" && !myRequested) || status === "none" ? (
        <Button
          onClick={() => onRequestAccess(c)}
          data-testid={`request-access-${c.lead_id}`}
          className="mt-4 w-full h-12 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-full">
          <Lock className="w-4 h-4 mr-2"/> Request to Handle
        </Button>
      ) : null}
    </div>
  );
}

function WantedPage({ takenTodayCount, okMonthCount, onDone }) {
  const [data, setData] = useState({ cases: [], score: 100, vip: false, boost: null, boost_state: "none", empty_reason: null, hint: null });
  const [reqDlg, setReqDlg] = useState(null);
  const [reqCount, setReqCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const load = () => api.get("/bank/wanted").then(r => setData(r.data)).catch(e => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const take = async (id) => { try { await api.post(`/bank/take/${id}`); toast.success("Case taken"); load(); onDone && onDone(); } catch (e) { toast.error(errorMessage(e)); } };
  const openRequest = (c) => { setReqDlg(c); setReqCount(c.required_staff_count || 1); };
  const submitRequest = async () => {
    if (!reqDlg) return;
    setSubmitting(true);
    try {
      await api.post(`/bank/leads/${reqDlg.lead_id}/request-access`, { required_count: reqCount });
      toast.success("Access requested · Telecaller will assign shortly");
      setReqDlg(null); load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <BankFuelMeter takenToday={takenTodayCount} okThisMonth={okMonthCount} score={data.score} vip={data.vip}/>

      {data.boost && <div className="rounded-2xl overflow-hidden"><BoostCountdownCard boost={data.boost} onWindowChange={load}/></div>}

      {data.cases.length === 0 ? (
        <EmptyWantedState boostState={data.boost_state} emptyReason={data.empty_reason} hint={data.hint} boost={data.boost}/>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="wanted-grid">
          {data.cases.map(c => (
            <WantedCard key={c.lead_id} c={c} onTake={take} onRequestAccess={openRequest}/>
          ))}
        </div>
      )}

      {/* Request-access dialog */}
      <Dialog open={!!reqDlg} onOpenChange={(o) => !o && setReqDlg(null)}>
        <DialogContent className="bg-[#141A2E] border-[#1E2742] text-white max-w-md" data-testid="request-access-dialog">
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2"><Users className="w-4 h-4"/> Request To Handle Case</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-slate-700 bg-[#0F1424] p-3 text-xs text-slate-400">
              Customer details stay <span className="text-slate-200 font-semibold">masked</span> until the Telecalling Team assigns this case to you.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1.5">How many staff members do you need? (1-3)</label>
              <Input type="number" min={1} max={3} data-testid="required-count-input" value={reqCount}
                onChange={e => setReqCount(Math.max(1, Math.min(3, parseInt(e.target.value || "1", 10))))}
                className="bg-[#0F1424] border-slate-700 text-white"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setReqDlg(null)} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">Cancel</Button>
            <Button data-testid="submit-request-access" onClick={submitRequest} disabled={submitting}
              className="bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-full">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyWantedState({ boostState, emptyReason, hint, boost }) {
  const isLive = boostState === "live";
  const tone =
    emptyReason === "inactive_staff" ? "bg-orange-500/10 border-orange-500/30 text-orange-300"
    : isLive ? "bg-[#25D366]/10 border-[#25D366]/40 text-emerald-300"
    : "bg-[#141A2E] border-[#1E2742] text-slate-300";
  return (
    <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-8 text-center" data-testid="wanted-empty">
      <div className="mx-auto w-16 h-16 rounded-full bg-slate-800/60 flex items-center justify-center mb-3">
        <Rocket className={`w-8 h-8 ${isLive ? "text-emerald-400 animate-pulse" : "text-slate-500"}`}/>
      </div>
      <div className="text-white font-bold text-lg">
        {emptyReason === "inactive_staff" ? "Your profile is not active"
          : isLive ? "Boost is LIVE — waiting for files"
          : "No cases scheduled"}
      </div>
      <div className="text-slate-500 text-sm mt-1 leading-relaxed max-w-md mx-auto">
        {hint || (isLive
          ? `No cases in ${boost?.district || "your district"} — ask Admin to add customer files.`
          : "Cases will appear when Admin boosts your district.")}
      </div>
      {boost && (
        <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest ${tone}`} data-testid="wanted-debug-badge">
          <span className={`w-2 h-2 rounded-full ${isLive ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`}/>
          Boost {boost.state?.toUpperCase() || "?"} · {boost.district} · {boost.start_time}–{boost.end_time} IST
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Referral Card — full width, no overflow (fixes the client complaint)
// Uses ReferralButton directly since it already renders the full mobile-friendly card.
// ============================================================================
function ReferralCard() {
  return <ReferralButton/>;
}

// ============================================================================
// My Taken (24h SLA) Page — redesigned card with big buttons + call link
// ============================================================================
function TakenCard({ c, onOK, onReturn, onApproval }) {
  const allApproved = APPROVALS.every(a => c[`${a.key}_status`] === "approved");
  return (
    <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid={`taken-${c.lead_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-mono-alt text-[#C5A028] text-xs">{c.lead_id}</div>
        <SLABadge24h takenAt={c.taken_at}/>
      </div>
      <div className="font-heading text-xl text-white leading-tight break-words">{c.name}</div>
      <div className="mt-1 space-y-1 text-sm">
        {c.phone && (
          <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-emerald-300 active:text-emerald-200">
            <Phone className="w-3.5 h-3.5"/><span className="font-mono-alt">{c.phone}</span>
          </a>
        )}
        <div className="text-white flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5"/>{parseFloat(c.amount).toLocaleString("en-IN")} · <span className="text-slate-400 uppercase text-xs tracking-wider">{c.loan_type}</span></div>
        {c.sourced_by_name && (
          <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap" data-testid={`sourced-by-${c.lead_id}`}>
            <span className="uppercase tracking-widest text-[9px] text-[#C5A028]">Sourced by</span>
            <span className="text-slate-300 font-semibold">{c.sourced_by_name}</span>
            {c.sourced_by_phone && (
              <a href={`tel:${c.sourced_by_phone}`} className="text-emerald-300 font-mono-alt hover:underline inline-flex items-center gap-1">
                <Phone className="w-3 h-3"/>{c.sourced_by_phone}
              </a>
            )}
          </div>
        )}
      </div>

      {c.location?.lat != null && c.location?.lng != null && (
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${c.location.lat},${c.location.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`navigate-${c.lead_id}`}
          className="mt-3 w-full flex items-center justify-center gap-2 h-11 bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 text-emerald-300 font-bold rounded-full transition text-sm">
          <Navigation className="w-4 h-4"/> Navigate to Customer
        </a>
      )}

      {/* Approvals — bank staff only */}
      <div className="mt-4 rounded-xl bg-[#0F1424] border border-[#1E2742] p-3">
        <div className="text-[10px] uppercase tracking-widest text-[#C5A028] font-bold mb-2">Approvals · Bank Staff Only</div>
        <div className="grid grid-cols-3 gap-2">
          {APPROVALS.map(a => {
            const s = c[`${a.key}_status`];
            const dotCls = s === "approved" ? "bg-emerald-500" : s === "rejected" ? "bg-red-500" : "bg-slate-600";
            return (
              <div key={a.key} className="text-center" data-testid={`approval-${a.key}-${c.lead_id}`}>
                <div className="flex items-center justify-center gap-1 mb-1.5">
                  <span className={`w-2 h-2 rounded-full ${dotCls}`}/>
                  <span className="text-xs text-white font-bold">{a.label}</span>
                </div>
                {s ? (
                  <div className={`text-[9px] uppercase tracking-widest ${s === "approved" ? "text-emerald-400" : "text-red-400"}`}>{s}</div>
                ) : (
                  <div className="flex gap-1.5 justify-center">
                    <button
                      type="button"
                      onClick={() => onApproval(c.lead_id, a.key, "approve")}
                      data-testid={`approve-${a.key}-${c.lead_id}`}
                      className="w-7 h-7 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 flex items-center justify-center active:scale-90 transition"
                    ><CheckCircle2 className="w-3.5 h-3.5"/></button>
                    <button
                      type="button"
                      onClick={() => onApproval(c.lead_id, a.key, "reject")}
                      data-testid={`reject-${a.key}-${c.lead_id}`}
                      className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-400 text-white flex items-center justify-center active:scale-90 transition"
                    ><XCircle className="w-3.5 h-3.5"/></button>
                  </div>
                )}
                {c[`${a.key}_remarks`] && (
                  <div className="text-[9px] text-slate-500 italic mt-1 line-clamp-2" title={c[`${a.key}_remarks`]}>{c[`${a.key}_remarks`]}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <Button
          onClick={() => onOK(c.lead_id)}
          data-testid={`ok-${c.lead_id}`}
          disabled={!allApproved}
          title={allApproved ? "" : "Approve CIBIL, Legal & Tech first"}
          className="h-12 bg-[#10B981] hover:bg-emerald-500 text-white font-bold rounded-full disabled:opacity-40 disabled:cursor-not-allowed">
          <CheckCircle2 className="w-4 h-4 mr-2"/> OK
        </Button>
        <Button
          onClick={() => onReturn(c.lead_id)}
          data-testid={`return-${c.lead_id}`}
          className="h-12 bg-[#2A3441] hover:bg-slate-700 text-white font-bold rounded-full">
          <XCircle className="w-4 h-4 mr-2"/> Return
        </Button>
      </div>
      {c.wa_group_invite_link ? (
        <a
          href={c.wa_group_invite_link}
          target="_blank" rel="noopener noreferrer"
          data-testid={`join-wa-group-${c.lead_id}`}
          className="mt-2 h-11 w-full rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold flex items-center justify-center gap-2 text-sm"
        >
          <MessagesSquare className="w-4 h-4"/> Join WhatsApp Group
        </a>
      ) : (
        <div className="mt-2 h-11 w-full rounded-full bg-orange-500/10 border border-orange-500/40 text-orange-300 font-bold flex items-center justify-center gap-2 text-xs" data-testid={`wa-waiting-${c.lead_id}`}>
          <Clock className="w-4 h-4"/> Waiting for telecaller to create WA group
        </div>
      )}
    </div>
  );
}

function TakenPage({ cases, reload, onCountsChange }) {
  const [returning, setReturning] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [approvalDlg, setApprovalDlg] = useState(null);
  const [remarks, setRemarks] = useState("");

  const markOk = async (id) => {
    try {
      await api.post(`/bank/taken/${id}/ok`);
      toast.success("Marked OK · Payment link sent");
      reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const doReturn = async () => {
    try {
      const r = await api.post(`/bank/taken/${returning}/return`, { reason, notes });
      toast.success(r.data.penalized ? "Returned (score -10%)" : "Returned to pool");
      setReturning(null); setReason(""); setNotes(""); reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const submitApproval = async () => {
    if (!approvalDlg) return;
    try {
      const res = await api.post(`/bank/taken/${approvalDlg.leadId}/approve/${approvalDlg.stage}`, {
        decision: approvalDlg.decision, remarks: remarks.trim(),
      });
      if (res.data.returned_to_pool) {
        toast.success(`${approvalDlg.stage.toUpperCase()} rejected · Sent back to Bank Pool`);
      } else {
        toast.success(`${approvalDlg.stage.toUpperCase()} ${approvalDlg.decision === "approve" ? "approved" : "rejected"}`);
      }
      setApprovalDlg(null); setRemarks(""); reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-3">
      {cases.length === 0 ? (
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-8 text-center text-slate-500" data-testid="taken-empty">
          <div className="mx-auto w-16 h-16 rounded-full bg-slate-800/60 flex items-center justify-center mb-3">
            <Briefcase className="w-8 h-8 text-slate-500"/>
          </div>
          <div className="text-white font-bold text-lg">No taken cases</div>
          <div className="text-sm mt-1">Grab one from Wanted List.</div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="taken-grid">
          {cases.map(c => (
            <TakenCard
              key={c.lead_id}
              c={c}
              onOK={markOk}
              onReturn={(id) => setReturning(id)}
              onApproval={(leadId, stage, decision) => { setApprovalDlg({ leadId, stage, decision }); setRemarks(""); }}
            />
          ))}
        </div>
      )}

      {/* Return dialog */}
      <Dialog open={!!returning} onOpenChange={(o) => !o && setReturning(null)}>
        <DialogContent className="bg-[#141A2E] border-[#1E2742] text-white">
          <DialogHeader><DialogTitle>Return this case</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="return-reason" className="bg-[#0F1424] border-slate-700"><SelectValue placeholder="Reason"/></SelectTrigger>
              <SelectContent className="bg-[#141A2E] text-white border-slate-700">
                {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} className="bg-[#0F1424] border-slate-700"/>
            <Button data-testid="confirm-return" onClick={doReturn} disabled={!reason} className="w-full bg-red-500 hover:bg-red-400 text-white font-bold rounded-full">
              Confirm Return
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval dialog */}
      <Dialog open={!!approvalDlg} onOpenChange={(o) => !o && setApprovalDlg(null)}>
        <DialogContent className="bg-[#141A2E] border-[#1E2742] text-white" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle>{approvalDlg?.decision === "approve" ? "Approve" : "Reject"} {approvalDlg?.stage?.toUpperCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {approvalDlg?.decision === "reject" && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200">
                <div className="font-bold uppercase tracking-widest mb-1">Heads up</div>
                <div className="text-red-200/80">This reject sends the case back to Bank Pool. All approvals reset.</div>
              </div>
            )}
            <Textarea
              data-testid="approval-remarks"
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder={approvalDlg?.decision === "reject" ? "Reason for rejection…" : "Remarks…"}
              className="bg-[#0F1424] border-slate-700"/>
            <Button
              data-testid="approval-submit"
              onClick={submitApproval}
              disabled={approvalDlg?.decision === "reject" && !remarks.trim()}
              className={`w-full font-bold rounded-full ${approvalDlg?.decision === "approve" ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900" : "bg-red-500 hover:bg-red-400 text-white"} disabled:opacity-40`}>
              Confirm {approvalDlg?.decision === "approve" ? "Approval" : "Rejection & Return"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// My OK Cases (30d) Page
// ============================================================================
function OKPage({ cases, reload }) {
  const disburse = async (id) => {
    try { await api.post(`/bank/ok-cases/${id}/disburse`); toast.success("Disbursed"); reload(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const activeCount = cases.filter(c => !c.disbursed).length;
  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid="ok-slots-card">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Active OK Slots</div>
        <div className="flex items-center gap-3">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                i < activeCount
                  ? "bg-[#C5A028]/25 border-[#C5A028] text-[#C5A028]"
                  : "bg-[#2A3441] border-slate-700 text-slate-600"
              }`}
              data-testid={`ok-slot-${i}`}
            >
              <CheckCircle2 className="w-5 h-5"/>
            </div>
          ))}
          <div className="ml-auto text-right">
            <div className="font-mono-alt text-white text-lg">{activeCount} / 3</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Used</div>
          </div>
        </div>
      </div>
      {cases.length === 0 ? (
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-8 text-center text-slate-500 min-h-[100px] flex flex-col items-center justify-center" data-testid="ok-empty">
          <CheckSquare className="w-8 h-8 text-slate-600 mb-2"/>
          <div className="text-slate-400">No OK cases yet.</div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="ok-grid">
          {cases.map(c => (
            <div key={c.lead_id} className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid={`ok-case-${c.lead_id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-mono-alt text-[#C5A028] text-xs">{c.lead_id}</div>
                <Timer30d okAt={c.ok_at}/>
              </div>
              <div className="font-heading text-lg text-white">{c.name}</div>
              <div className="text-sm text-slate-400 mt-1 space-y-0.5">
                <div className="text-white flex items-center gap-1.5"><IndianRupee className="w-3 h-3"/>{parseFloat(c.amount).toLocaleString("en-IN")} · {c.loan_type}</div>
                <div>Fees ₹{(c.fees_amount || 0).toLocaleString("en-IN")} · <span className={c.payment_status === "paid" ? "text-emerald-400" : "text-[#FF8C00]"}>{c.payment_status}</span></div>
              </div>
              <Button
                onClick={() => disburse(c.lead_id)}
                disabled={c.payment_status !== "paid"}
                data-testid={`disburse-${c.lead_id}`}
                className="mt-4 w-full h-11 bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-800 disabled:text-slate-500 text-slate-900 font-bold rounded-full">
                {c.payment_status === "paid" ? "Mark Disbursed" : "Awaiting Customer Payment"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Profile Page — mobile-only tab (avatar, contact info, score, logout)
// ============================================================================
function ProfilePage({ score = 100, vip = false }) {
  const { user, logout } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const doLogout = () => { logout(); window.location.href = "/login"; };
  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-5 text-center" data-testid="profile-card">
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#C5A028] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-bold text-2xl border-2 border-[#C5A028]/50 overflow-hidden">
          {user?.photo_b64 ? <img src={user.photo_b64} alt="avatar" className="w-full h-full object-cover"/> :
            (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="font-heading text-2xl text-white mt-3">{user?.name || "—"}</div>
        <div className="text-sm text-slate-500 font-mono-alt">{user?.phone || "—"}</div>
        <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
          <Badge className="bg-[#0F1424] text-slate-300 border-slate-700 text-[10px] uppercase tracking-widest">
            <MapPin className="w-2.5 h-2.5 mr-1"/>{user?.district || "—"}
            {user?.branch_name ? ` · ${user.branch_name}` : ""}
          </Badge>
          {user?.bank_name && (
            <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30 text-[10px]">{user.bank_name}</Badge>
          )}
          {vip && (
            <Badge className="bg-[#C5A028]/20 text-[#C5A028] border-[#C5A028]/40 text-[10px]">
              <Sparkles className="w-2.5 h-2.5 mr-1"/> VIP
            </Badge>
          )}
        </div>
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          data-testid="edit-profile-btn"
          className="mt-4 w-full h-11 rounded-full bg-[#FFD700] hover:bg-[#f0c800] text-slate-900 font-black text-sm">
          Edit Profile
        </button>
      </div>

      <BankStaffEditProfileDialog open={editOpen} onClose={() => setEditOpen(false)}/>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4 text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Score</div>
          <div className={`font-display text-3xl mt-1 ${score >= 90 ? "text-emerald-400" : score >= 70 ? "text-[#C5A028]" : "text-red-400"}`}>{score}%</div>
        </div>
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4 text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Role</div>
          <div className="font-heading text-lg text-white mt-1">Bank Staff</div>
        </div>
      </div>

      <button
        type="button"
        onClick={doLogout}
        data-testid="profile-logout"
        className="w-full h-12 rounded-full bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25 font-bold flex items-center justify-center gap-2 mt-2"
      >
        <LogOut className="w-4 h-4"/> Logout
      </button>

      <div className="text-center text-[10px] text-slate-600 uppercase tracking-widest mt-6">
        www.sakthiloanconsultancy.com
      </div>
    </div>
  );
}

// ============================================================================
// Root dashboard
// ============================================================================
export default function BankStaffDashboard() {
  const [active, setActive] = useState("refer");
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [takenCases, setTakenCases] = useState([]);
  const [okCases, setOkCases] = useState([]);

  const loadTaken = () => api.get("/bank/taken").then(r => setTakenCases(r.data)).catch(() => {});
  const loadOK = () => api.get("/bank/ok-cases").then(r => setOkCases(r.data)).catch(() => {});
  useEffect(() => { loadTaken(); loadOK(); }, []);

  const hasOverdue = useMemo(() => {
    const now = Date.now();
    return takenCases.some(c => c.taken_at && (now - new Date(c.taken_at).getTime()) > 24 * 3600 * 1000);
  }, [takenCases]);

  const takenTodayCount = useMemo(() => {
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    return takenCases.filter(c => c.taken_at && new Date(c.taken_at).getTime() >= startOfDay.getTime()).length;
  }, [takenCases]);

  const okMonthCount = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    return okCases.filter(c => c.ok_at && new Date(c.ok_at).getTime() >= monthStart).length;
  }, [okCases]);

  const pageTitle = PAGE_TITLES[active] || "Bank Staff";
  const showHeaderAction = active !== "profile" && active !== "refer";

  return (
    <BankStaffShell
      active={active}
      onNav={setActive}
      pageTitle={pageTitle}
      hasOverdue={hasOverdue}
      headerAction={showHeaderAction ? (
        <AddCustomerHeaderPill onOpen={() => setAddCustomerOpen(true)}/>
      ) : null}
    >
      {active === "refer"   && <BankStaffReferPage/>}
      {active === "wanted"  && <WantedPage takenTodayCount={takenTodayCount} okMonthCount={okMonthCount} onDone={loadTaken}/>}
      {active === "taken"   && <TakenPage cases={takenCases} reload={() => { loadTaken(); loadOK(); }}/>}
      {active === "ok"      && <OKPage cases={okCases} reload={loadOK}/>}
      {active === "profile" && <ProfilePage/>}

      {/* Hidden UniversalLeadAdd — opened programmatically from header pill */}
      <div className="fixed left-[-9999px] top-0 pointer-events-none opacity-0" aria-hidden="true">
        <UniversalLeadAdd/>
      </div>

      {/* Actual open trigger: reveal UniversalLeadAdd portal via its own dialog. We
          use a lightweight prompt because UniversalLeadAdd manages its own state.
          Clicking the header pill scrolls the hidden component into view + focuses
          its button. Simpler: render an overlay dialog on demand. */}
      <AddCustomerDialog open={addCustomerOpen} onClose={() => setAddCustomerOpen(false)}/>
    </BankStaffShell>
  );
}

// ------------------- Add Customer dialog wrapper --------------------------
function AddCustomerDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141A2E] border-[#1E2742] text-white max-w-md" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <UserPlus className="w-4 h-4 text-[#FF8C00]"/> Add Customer
          </DialogTitle>
        </DialogHeader>
        <div className="pt-2">
          {/* Reuse the existing UniversalLeadAdd. Its trigger button becomes the primary CTA. */}
          <UniversalLeadAdd/>
        </div>
      </DialogContent>
    </Dialog>
  );
}
